import type { BoqEstimate } from "../../api/boq";
import { formatDate } from "../../api/dates";
import { formatMoney, formatQuantity } from "../../api/money";

const SERIES_RULE: Record<string, string> = {
  stated: "the series the QCP states",
  dearer: "the dearer of EN and ES, because the QCP does not say which",
  only: "the only kit at this size",
};

/** What a pre-issued bill was priced on before anybody visited: the kit or the
 * rate-card worst case, the journey its mileage line charges, the notes the
 * server wrote about what it could not settle, and the model the visit
 * recorded once it has. Every sentence is the server's figures; nothing here
 * works money out. */
export default function BoqEstimatePanel({ estimate }: { estimate: BoqEstimate }) {
  const kit = estimate.kit;
  const journey = estimate.journey;
  const basis = kit
    ? `Priced from the ${kit.size_label} service kit${kit.supplier ? ` on ${kit.supplier.name}'s price list` : ""}${
        kit.price_list_version !== null ? ` (version ${kit.price_list_version})` : ""
      }, ${SERIES_RULE[kit.series_rule ?? ""] ?? "as the price list carries it"}: K1 ${formatMoney(estimate.k1_estimate)}.`
    : `Priced on the rate-card worst case: no service kit is held for this make and size${
        estimate.k1_estimate ? `; parts ${formatMoney(estimate.k1_estimate)}` : ""
      }.`;
  const mileage =
    journey.km === null
      ? "No mileage was estimated."
      : `Mileage ${formatQuantity(journey.km)} km${journey.meeting_point ? ` via ${journey.meeting_point.name}` : ""}${
          journey.approximate ? ", a straight-line estimate" : ""
        }.`;
  const confirmed = estimate.model_confirmed;
  return (
    <section className="cm-block" aria-label="Priced before the visit">
      <h3>Priced before the visit</h3>
      <p className="cm-doc-note">{basis}</p>
      <p className="cm-doc-note">{mileage}</p>
      {confirmed ? (
        <p className="notice">
          The visit recorded {confirmed.model} on {formatDate(confirmed.at)}
          {confirmed.by ? ` (${confirmed.by})` : ""}.
        </p>
      ) : null}
      {estimate.notes.length > 0 ? (
        <ul className="cm-notes">
          {estimate.notes.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
